import { v4 as uuidv4 } from "uuid";
import FingerprintJS from "@fingerprintjs/fingerprintjs";

export interface DeviceInfo {
  userAgent: string;
  platform: string;
  os: string;
  language: string;
  screenResolution: string;
  timezone: string;
  colorDepth: number;
  hardwareConcurrency: number;
  deviceMemory?: string | number;
  touchSupport: boolean;
  deviceId: string;
  fingerprint: string | null;
}

export const useDeviceId = () => {
  const deviceInfoState = useState<DeviceInfo | null>("deviceInfo", () => null);

  const DEVICE_ID_KEY = "device_id";
  const DEVICE_FINGERPRINT_KEY = "device_fingerprint";
  const DEVICE_INFO_KEY = "device_info";

  const generateDeviceId = (): string => {
    if (import.meta.server) return "";
    return (
      Math.floor(Math.random() * 99999).toString() +
      "_" +
      uuidv4().replaceAll("-", "_")
    ).toLocaleUpperCase();
  };

  const getDeviceFingerprint = async (): Promise<string | null> => {
    if (import.meta.server) return "";

    let fingerprint = localStorage.getItem(DEVICE_FINGERPRINT_KEY);

    if (!fingerprint) {
      try {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        fingerprint = result.visitorId;
        localStorage.setItem(DEVICE_FINGERPRINT_KEY, fingerprint);
      } catch (error: unknown) {
        console.error(error);
        fingerprint = null;
      }
    }

    return fingerprint;
  };

  const getDeviceInfo = async (): Promise<DeviceInfo | null> => {
    if (isServerSide()) return null;

    if (deviceInfoState.value) {
      return deviceInfoState.value;
    }

    const cachedInfo = localStorage.getItem(DEVICE_INFO_KEY);
    if (cachedInfo) {
      deviceInfoState.value = JSON.parse(cachedInfo);
      return deviceInfoState.value;
    }

    const deviceInfo: DeviceInfo = {
      userAgent: navigator.userAgent,
      platform: "web",
      os: navigator.platform || detectOS(),
      language: navigator.language,
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      colorDepth: screen.colorDepth,
      hardwareConcurrency: navigator.hardwareConcurrency,
      touchSupport: "ontouchstart" in window,
      deviceId: generateDeviceId(),
      fingerprint: await getDeviceFingerprint(),
    };

    localStorage.setItem(DEVICE_INFO_KEY, JSON.stringify(deviceInfo));
    deviceInfoState.value = deviceInfo;
    return deviceInfo;
  };

  const detectOS = (): string => {
    const ua = navigator.userAgent.toLowerCase();
    if (/windows nt|win/.test(ua)) return "Windows";
    if (/mac os x|macintosh/.test(ua)) return "macOS";
    if (/android/.test(ua)) return "Android";
    if (/iphone|ipad|ipod/.test(ua)) return "iOS";
    if (/linux/.test(ua)) return "Linux";
    return "Unknown";
  };

  const clearDeviceData = () => {
    if (isServerSide()) return;
    localStorage.removeItem(DEVICE_INFO_KEY);
    deviceInfoState.value = null;
  };

  const load = async () => {
    await getDeviceInfo();
  };

  const getDeviceId = () => {
    return deviceInfoState.value?.deviceId || null;
  };

  const isDeviceRegistered = (): boolean => {
    if (isServerSide()) return false;
    return !!localStorage.getItem(DEVICE_ID_KEY);
  };

  return {
    getDeviceInfo,
    clearDeviceData,
    deviceInfo: computed(() => deviceInfoState.value),
    isDeviceRegistered,
    load,
    getDeviceId,
  };
};
